import React, { useEffect, useState, useRef, createElement, Component } from 'react';
import { BuildingIcon, PlusIcon, SearchIcon, MapPinIcon, PhoneIcon, UserIcon, EditIcon, TrashIcon, NavigationIcon, CheckCircleIcon, XCircleIcon, MapIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { Client } from '../types';
import L from 'leaflet';
import { LeafletMap } from '../components/ui/LeafletMap';
import { hasGoogleApiKey, searchGooglePlaces } from '../lib/googleMaps';
export function ClientsPage() {
  const {
    user,
    addActivityLog
  } = useAuth();
  const {
    clients,
    users,
    addClient,
    updateClient,
    deleteClient,
    getCurrentLocation
  } = useApp();
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [activeTab, setActiveTab] = useState<'info' | 'location'>('info');
  const [isSaving, setIsSaving] = useState(false);
  // Address search state
  const [addressSearch, setAddressSearch] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{
    display_name: string;
    lat: string;
    lon: string;
  }>>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Form state
  const [formData, setFormData] = useState({
    codigo: '',
    nombre: '',
    telefono: '',
    ciudad: '',
    limite: '',
    cedula: '',
    rnc: '',
    ruta: '',
    direccion: '',
    vendedorId: '',
    vendedorNombre: '',
    dependencia: '',
    zona: '',
    lat: null as number | null,
    lng: null as number | null
  });
  // Map refs
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const userLocationMarkerRef = useRef<L.Marker | null>(null);
  const mapDestroyedRef = useRef(false);
  const vendors = users.filter(u => u.rolNombre !== 'Administrador' && u.activo && u.permisos?.['new-visit'] === true);
  const vendorOptions = [{
    value: '',
    label: 'Sin vendedor asignado'
  }, ...vendors.map(v => ({
    value: v.id,
    label: v.nombre
  }))];
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 8;
  const filteredClients = clients.filter(c => {
    const query = searchQuery.toLowerCase();
    return c.nombre.toLowerCase().includes(query) || c.zona.toLowerCase().includes(query) || c.codigo && c.codigo.toLowerCase().includes(query);
  });
  const totalPages = Math.ceil(filteredClients.length / ITEMS_PER_PAGE);
  const paginatedClients = filteredClients.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const handleMapReady = (map: L.Map) => {
    mapInstanceRef.current = map;
    mapDestroyedRef.current = false;
    // Setup map interactions
    map.on('click', (e: L.LeafletMouseEvent) => {
      if (mapDestroyedRef.current) return;
      const {
        lat,
        lng
      } = e.latlng;
      setFormData(prev => ({
        ...prev,
        lat,
        lng
      }));
      updateMarker(lat, lng);
    });
    // Initial marker if data exists
    if (formData.lat && formData.lng) {
      updateMarker(formData.lat, formData.lng);
      map.setView([formData.lat, formData.lng], 15);
    }
    // Show user location if available
    getCurrentLocation().then(loc => {
      if (!loc || mapDestroyedRef.current) return;
      const userIcon = L.divIcon({
        html: `
          <div style="position:relative;width:20px;height:20px;">
            <div style="position:absolute;inset:0;background:#3b82f6;border-radius:50%;opacity:0.25;animation:ping 1.5s cubic-bezier(0,0,0.2,1) infinite;"></div>
            <div style="position:absolute;inset:3px;background:#3b82f6;border-radius:50%;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.3);"></div>
          </div>
        `,
        className: '',
        iconSize: [20, 20],
        iconAnchor: [10, 10]
      });
      const userMarker = L.marker([loc.lat, loc.lng], {
        icon: userIcon
      }).addTo(map).bindPopup('<div style="font-family:system-ui;font-size:13px;font-weight:700;color:#1e40af;">📍 Tu ubicación actual</div>', {
        maxWidth: 180
      });
      userLocationMarkerRef.current = userMarker;
      // If no client location set yet, center on user
      if (!formData.lat && !formData.lng) {
        map.setView([loc.lat, loc.lng], 15);
      }
    });
  };
  const updateMarker = (lat: number, lng: number) => {
    if (!mapInstanceRef.current) return;
    // Custom red teardrop SVG icon — avoids broken default Leaflet icon
    const pinIcon = L.divIcon({
      html: `
        <div style="position:relative;width:30px;height:40px;">
          <svg viewBox="0 0 24 32" width="30" height="40" xmlns="http://www.w3.org/2000/svg"
            style="filter:drop-shadow(0 2px 4px rgba(0,0,0,0.35));display:block;">
            <path d="M12 0C7.58 0 4 3.58 4 8c0 5.25 8 16 8 16s8-10.75 8-16c0-4.42-3.58-8-8-8z"
              fill="#dc2626" stroke="white" stroke-width="1.5"/>
            <circle cx="12" cy="8" r="3.5" fill="white" opacity="0.95"/>
          </svg>
        </div>`,
      className: '',
      iconSize: [30, 40],
      iconAnchor: [15, 40]
    });
    if (markerRef.current) {
      markerRef.current.setLatLng([lat, lng]);
    } else {
      const marker = L.marker([lat, lng], {
        draggable: true,
        icon: pinIcon
      }).addTo(mapInstanceRef.current);
      marker.on('dragend', () => {
        const pos = marker.getLatLng();
        setFormData(prev => ({
          ...prev,
          lat: pos.lat,
          lng: pos.lng
        }));
      });
      markerRef.current = marker;
    }
  };
  // Update marker when GPS/search sets location
  useEffect(() => {
    if (mapDestroyedRef.current) return;
    if (!mapInstanceRef.current || !formData.lat || !formData.lng) return;
    updateMarker(formData.lat, formData.lng);
    mapInstanceRef.current.setView([formData.lat, formData.lng], 15);
  }, [formData.lat, formData.lng]);
  const openAddModal = () => {
    setEditingClient(null);
    setFormData({
      codigo: '',
      nombre: '',
      telefono: '',
      ciudad: '',
      limite: '',
      cedula: '',
      rnc: '',
      ruta: '',
      direccion: '',
      vendedorId: '',
      vendedorNombre: '',
      dependencia: '',
      zona: '',
      lat: null,
      lng: null
    });
    setAddressSearch('');
    setSearchResults([]);
    setShowResults(false);
    setActiveTab('info');
    setIsModalOpen(true);
  };
  const openEditModal = (client: Client) => {
    setEditingClient(client);
    setFormData({
      codigo: client.codigo || '',
      nombre: client.nombre,
      telefono: client.telefono || '',
      ciudad: client.ciudad || '',
      limite: client.limite ? String(client.limite) : '',
      cedula: client.cedula || '',
      rnc: client.rnc || '',
      ruta: client.ruta || '',
      direccion: client.direccion,
      vendedorId: client.vendedorId || '',
      vendedorNombre: client.vendedorNombre || '',
      dependencia: client.dependencia || '',
      zona: client.zona,
      lat: client.lat || null,
      lng: client.lng || null
    });
    setAddressSearch('');
    setSearchResults([]);
    setShowResults(false);
    setActiveTab('info');
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
    setEditingClient(null);
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
      markerRef.current = null;
    }
  };
  const handleUseCurrentLocation = async () => {
    setLocationError('');
    setIsGettingLocation(true);
    try {
      await new Promise<void>((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('GPS no disponible en este dispositivo'));
          return;
        }
        navigator.geolocation.getCurrentPosition(pos => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          setFormData(prev => ({
            ...prev,
            lat,
            lng
          }));
          // Update user location marker on map
          if (mapInstanceRef.current && userLocationMarkerRef.current) {
            userLocationMarkerRef.current.setLatLng([lat, lng]);
            mapInstanceRef.current.setView([lat, lng], 17);
          } else if (mapInstanceRef.current) {
            mapInstanceRef.current.setView([lat, lng], 17);
          }
          resolve();
        }, err => {
          if (err.code === err.PERMISSION_DENIED) {
            reject(new Error('Permiso de ubicación denegado. Actívalo en la configuración del navegador.'));
          } else if (err.code === err.POSITION_UNAVAILABLE) {
            reject(new Error('Ubicación no disponible. Verifica que el GPS esté activado.'));
          } else {
            reject(new Error('No se pudo obtener la ubicación. Intenta nuevamente.'));
          }
        }, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        });
      });
    } catch (err: any) {
      setLocationError(err.message);
    } finally {
      setIsGettingLocation(false);
    }
  };
  const handleSave = async () => {
    if (!formData.nombre || !formData.zona || !formData.direccion) return;
    setIsSaving(true);
    try {
      const selectedVendor = vendors.find(v => v.id === formData.vendedorId);
      const clientData = {
        codigo: formData.codigo,
        nombre: formData.nombre,
        telefono: formData.telefono,
        ciudad: formData.ciudad,
        limite: Number(formData.limite) || 0,
        cedula: formData.cedula,
        rnc: formData.rnc,
        ruta: formData.ruta,
        direccion: formData.direccion,
        vendedorId: formData.vendedorId || undefined,
        vendedorNombre: selectedVendor?.nombre || undefined,
        dependencia: formData.dependencia,
        zona: formData.zona,
        lat: formData.lat || undefined,
        lng: formData.lng || undefined
      };
      // We cast to any here because the addClient/updateClient types might not be fully updated in the context definition yet
      // but the underlying implementation handles the new fields if the migration ran.
      if (editingClient) {
        await updateClient(editingClient.id, clientData as any);
        addActivityLog('Editó farmacia', `Farmacia: ${formData.nombre} | Código: ${formData.codigo}`);
      } else {
        await addClient(clientData as any);
        addActivityLog('Creó farmacia', `Farmacia: ${formData.nombre} | Código: ${formData.codigo}`);
      }
      closeModal();
    } finally {
      setIsSaving(false);
    }
  };
  const handleDelete = async () => {
    if (!editingClient) return;
    setIsSaving(true);
    try {
      await deleteClient(editingClient.id);
      addActivityLog('Eliminó farmacia', `Farmacia: ${editingClient.nombre}`);
      setIsDeleteModalOpen(false);
      closeModal();
    } finally {
      setIsSaving(false);
    }
  };
  const handleAddressSearch = (query: string) => {
    setAddressSearch(query);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (query.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }
    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        // Try Google Places first if API key is available
        if (hasGoogleApiKey()) {
          const googleResults = await searchGooglePlaces(query);
          if (googleResults.length > 0) {
            setSearchResults(googleResults);
            setShowResults(true);
            setIsSearching(false);
            return;
          }
        }
        // Fallback to free geocoders
        const searchQuery = encodeURIComponent(query);
        const [photonRes, nominatimRes, nominatimPOI] = await Promise.all([fetch(`https://photon.komoot.io/api/?q=${searchQuery}&limit=8&lang=es&lat=18.48&lon=-69.93`).then(r => r.json()).catch(() => ({
          features: []
        })), fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${searchQuery}&limit=6&addressdetails=1&accept-language=es`, {
          headers: {
            'Accept-Language': 'es'
          }
        }).then(r => r.json()).catch(() => []), query.toLowerCase().includes('farmacia') ? Promise.resolve([]) : fetch(`https://nominatim.openstreetmap.org/search?format=json&q=farmacia+${searchQuery}&limit=4&countrycodes=do&addressdetails=1&accept-language=es`, {
          headers: {
            'Accept-Language': 'es'
          }
        }).then(r => r.json()).catch(() => [])]);
        const seen = new Set<string>();
        const merged: Array<{
          display_name: string;
          lat: string;
          lon: string;
        }> = [];
        if (photonRes.features) {
          for (const f of photonRes.features) {
            const lat = String(f.geometry.coordinates[1]);
            const lon = String(f.geometry.coordinates[0]);
            const key = `${parseFloat(lat).toFixed(4)},${parseFloat(lon).toFixed(4)}`;
            if (!seen.has(key)) {
              seen.add(key);
              const props = f.properties || {};
              const parts = [props.name, props.street, props.city || props.district, props.state, props.country].filter(Boolean);
              merged.push({
                display_name: parts.join(', '),
                lat,
                lon
              });
            }
          }
        }
        for (const result of [...nominatimRes, ...nominatimPOI]) {
          const key = `${parseFloat(result.lat).toFixed(4)},${parseFloat(result.lon).toFixed(4)}`;
          if (!seen.has(key)) {
            seen.add(key);
            merged.push(result);
          }
        }
        setSearchResults(merged.slice(0, 12));
        setShowResults(true);
      } catch {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 350);
  };
  const formatDisplayName = (display_name: string) => {
    return display_name.replace(/, República Dominicana$/, '').replace(/, Dominican Republic$/, '');
  };
  const handleSelectAddress = (result: {
    display_name: string;
    lat: string;
    lon: string;
  }) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    setFormData(prev => ({
      ...prev,
      lat,
      lng
    }));
    setAddressSearch(result.display_name);
    setShowResults(false);
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: 'DOP',
      minimumFractionDigits: 0
    }).format(amount);
  };
  return <div className="space-y-4 sm:space-y-6" data-id="element-929" data-mp="2qymnmbdph2o">
      <PageHeader title="Farmacias" subtitle="Gestión de farmacias y ubicaciones" icon={<BuildingIcon className="w-6 h-6" data-id="element-931" data-mp="3pwze7vte19ae" />} data-id="element-930" data-mp="7rw4fxsh893n">
        
        <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto" data-id="element-932" data-mp="3pyaq6iwzlncl">
          <div className="relative flex-1 sm:w-64" data-id="element-933" data-mp="3txt3otmhaw5e">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-934" data-mp="3u0e6plyiji4q" />
            <input type="text" placeholder="Buscar por nombre, código o zona..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] shadow-sm" data-id="element-935" data-mp="1o3s0urxc51r5" />
            
          </div>
          {isAdmin && <Button onClick={openAddModal} leftIcon={<PlusIcon className="w-4 h-4" data-id="element-937" data-mp="2n4dd8eky0168" />} data-id="element-936" data-mp="2s49liq0oaerl">
            
              <span className="hidden sm:inline" data-id="element-938" data-mp="2n5y7e33chi3k">Nueva Farmacia</span>
              <span className="sm:hidden" data-id="element-939" data-mp="3juw3bomtzm2b">Nueva</span>
            </Button>}
        </div>
      </PageHeader>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 animate-stagger-1" data-id="element-940" data-mp="3fuca2w5dp2lg">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-5" data-id="element-941" data-mp="3b1aok5ao24sw">
          <div className="flex items-center gap-2.5 sm:gap-3" data-id="element-942" data-mp="36413t2y82hbe">
            <div className="p-2 sm:p-2.5 bg-blue-50 rounded-xl" data-id="element-943" data-mp="361etivjy5nph">
              <BuildingIcon className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" data-id="element-944" data-mp="7esg1uwvvg5c" />
            </div>
            <div data-id="element-945" data-mp="2ky1t701rtdn">
              <p className="text-xl sm:text-2xl font-bold text-gray-900" data-id="element-946" data-mp="2kfwlkveq4tl">
                {clients.length}
              </p>
              <p className="text-[11px] sm:text-xs text-gray-500 font-medium" data-id="element-947" data-mp="ewqqc1mw2v9m">
                Total Farmacias
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-5" data-id="element-948" data-mp="jxxvru1k7id4">
          <div className="flex items-center gap-2.5 sm:gap-3" data-id="element-949" data-mp="nyrm0u2h5996">
            <div className="p-2 sm:p-2.5 bg-emerald-50 rounded-xl" data-id="element-950" data-mp="sxb4gkhv02ws">
              <MapPinIcon className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" data-id="element-951" data-mp="xvmeav85mdf0" />
            </div>
            <div data-id="element-952" data-mp="12mck29khsia8">
              <p className="text-xl sm:text-2xl font-bold text-emerald-600" data-id="element-953" data-mp="12lt5n2u1wc0y">
                {clients.filter(c => c.lat && c.lng).length}
              </p>
              <p className="text-[11px] sm:text-xs text-gray-500 font-medium" data-id="element-954" data-mp="17nifif5jejng">
                Con GPS
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-5" data-id="element-955" data-mp="1gmvs3goyjoce">
          <div className="flex items-center gap-2.5 sm:gap-3" data-id="element-956" data-mp="21w13vwh0fsjl">
            <div className="p-2 sm:p-2.5 bg-orange-50 rounded-xl" data-id="element-957" data-mp="1wxzkfafo9r7l">
              <XCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" data-id="element-958" data-mp="1wxpvlq6d5q3h" />
            </div>
            <div data-id="element-959" data-mp="2buguzzg1f1t3">
              <p className="text-xl sm:text-2xl font-bold text-orange-600" data-id="element-960" data-mp="2bsw1md5glowt">
                {clients.filter(c => !c.lat || !c.lng).length}
              </p>
              <p className="text-[11px] sm:text-xs text-gray-500 font-medium" data-id="element-961" data-mp="1j7cm3jdtq3c1">
                Sin GPS
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-5" data-id="element-962" data-mp="1sw4bpkvewbjs">
          <div className="flex items-center gap-2.5 sm:gap-3" data-id="element-963" data-mp="1o5xn3l0wgr1j">
            <div className="p-2 sm:p-2.5 bg-purple-50 rounded-xl" data-id="element-964" data-mp="1o4ctbxmetc4q">
              <MapIcon className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" data-id="element-965" data-mp="10f1oo51xuxlb" />
            </div>
            <div data-id="element-966" data-mp="vhs0esqacrz2">
              <p className="text-xl sm:text-2xl font-bold text-purple-600" data-id="element-967" data-mp="vg7716fpjf2s">
                {new Set(clients.map(c => c.zona)).size}
              </p>
              <p className="text-[11px] sm:text-xs text-gray-500 font-medium" data-id="element-968" data-mp="3bv28g6l33ba6">
                Zonas
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Clients list */}
      <div className="animate-stagger-2" data-id="element-969" data-mp="324zf25a1vvbb">
        {filteredClients.length === 0 ? <div className="bg-white rounded-xl sm:rounded-2xl border border-slate-200/60 shadow-sm p-5 text-center animate-fade-in" data-id="element-970" data-mp="32363cbkkykwg">
            <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-gray-100 rounded-2xl mb-4" data-id="element-971" data-mp="3uug4qv6acy3u">
              <BuildingIcon className="w-7 h-7 sm:w-8 sm:h-8 text-gray-400" data-id="element-972" data-mp="3gtdfk9jzv7op" />
            </div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1" data-id="element-973" data-mp="3kupb0vpjun4t">
              {searchQuery ? 'No se encontraron farmacias' : 'No hay farmacias registradas'}
            </h3>
            <p className="text-gray-500 text-sm" data-id="element-974" data-mp="29hms3tzz9lhh">
              {searchQuery ? 'Intenta con otro término de búsqueda' : 'Agrega tu primera farmacia para comenzar'}
            </p>
          </div> : <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-975" data-mp="1fryh88bmjpj3">
            {/* ── MOBILE CARD VIEW (< sm) ── */}
            <div className="sm:hidden divide-y divide-gray-100" data-id="element-976" data-mp="11rfglm9hnlqy">
              {paginatedClients.map(client => <div key={client.id} className="p-3.5" data-id="element-977" data-mp="16oxg6m1m8oma">
                  <div className="flex items-start justify-between gap-2" data-id="element-978" data-mp="s1m1wce1uqih">
                    <div className="min-w-0 flex-1" data-id="element-979" data-mp="wsc8fsy7ozer">
                      <div className="flex items-center gap-2" data-id="element-980" data-mp="wu5fvagnfnnt">
                        <div className="p-1.5 bg-blue-50 rounded-lg flex-shrink-0" data-id="element-981" data-mp="j1z19st59oqh">
                          <BuildingIcon className="w-3.5 h-3.5 text-blue-600" data-id="element-982" data-mp="o00s4zwtasci" />
                        </div>
                        <p className="font-semibold text-gray-900 text-sm truncate" data-id="element-983" data-mp="92hzhmh0juzs">
                          {client.nombre}
                        </p>
                      </div>
                      {client.codigo && <p className="text-[11px] text-gray-400 font-mono mt-0.5 ml-8" data-id="element-984" data-mp="3ql5ootbsr0se">
                          {client.codigo}
                        </p>}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0" data-id="element-985" data-mp="4e7dzrwv9p8e">
                      {client.lat && client.lng ? <span className="w-5 h-5 flex items-center justify-center" data-id="element-986" data-mp="3vl1tuvw8cy9j">
                          <CheckCircleIcon className="w-4 h-4 text-emerald-500" data-id="element-987" data-mp="df5i0a7b1wqz" />
                        </span> : <span className="w-5 h-5 flex items-center justify-center" data-id="element-988" data-mp="dcsttfayeq5h">
                          <XCircleIcon className="w-4 h-4 text-gray-300" data-id="element-989" data-mp="9bhbluuet77b" />
                        </span>}
                      {isAdmin && <>
                          <button onClick={() => openEditModal(client)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-990" data-mp="i5dlobaaut6d">
                      
                            <EditIcon className="w-4 h-4" data-id="element-991" data-mp="s4l6fjjd5q3i" />
                          </button>
                          <button onClick={() => {
                    setEditingClient(client);
                    setIsDeleteModalOpen(true);
                  }} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-992" data-mp="z7j9m9qxgoxi">
                      
                            <TrashIcon className="w-4 h-4" data-id="element-993" data-mp="1dvvzcozf9bjk" />
                          </button>
                        </>}
                    </div>
                  </div>
                  <div className="mt-1.5 ml-8 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500" data-id="element-994" data-mp="1iuz2g3vud7ow">
                    {client.zona && client.zona !== 'null' && <span className="inline-flex items-center gap-1 text-blue-600 font-medium" data-id="element-995" data-mp="1rvnl5bw6jw84">
                        <MapPinIcon className="w-3 h-3" data-id="element-996" data-mp="1wv9z5nju1ghz" />
                        {client.zona}
                      </span>}
                    {client.telefono && <span className="inline-flex items-center gap-1" data-id="element-997" data-mp="3tpehlztmcy6z">
                        <PhoneIcon className="w-3 h-3" data-id="element-998" data-mp="2hs8vss1m5j9" />
                        {client.telefono}
                      </span>}
                    {client.vendedorNombre && <span className="inline-flex items-center gap-1" data-id="element-999" data-mp="3mc5en4hgl62u">
                        <UserIcon className="w-3 h-3" data-id="element-1000" data-mp="3ma2gtya2e8nk" />
                        {client.vendedorNombre}
                      </span>}
                    {client.limite > 0 && <span className="font-medium text-gray-700" data-id="element-1001" data-mp="3cer3nkeb19hx">
                        {formatCurrency(client.limite)}
                      </span>}
                  </div>
                  {client.direccion && <p className="mt-1 ml-8 text-[11px] text-gray-400 truncate" data-id="element-1002" data-mp="8wzeedrjz3nf">
                      {client.direccion}
                    </p>}
                </div>)}
            </div>

            {/* ── DESKTOP TABLE (sm+) ── */}
            <div className="hidden sm:block overflow-x-auto" data-id="element-1003" data-mp="3v3xnjaqu475b">
              <table className="w-full" data-id="element-1004" data-mp="2oowrl51t7oyq">
                <thead data-id="element-1005" data-mp="2opomednl1iny">
                  <tr className="bg-gray-50/80 border-b border-gray-100" data-id="element-1006" data-mp="2om0tngpyp0aa">
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-1007" data-mp="2jnz88p52covn">
                      Código
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-1008" data-mp="h6d7ogwtrx4w">
                      Nombre
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-1009" data-mp="7fsb0z0pp2om">
                      Teléfono
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-1010" data-mp="102d5w9gebwwx">
                      Ciudad
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-1011" data-mp="q4gxvqir9i31">
                      Límite
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-1012" data-mp="3avzf36n1h1xx">
                      Cédula
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-1013" data-mp="1nzsyix29wu4l">
                      RNC
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-1014" data-mp="1xwnvvcmt8oha">
                      Ruta
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-1015" data-mp="158rzya52f0lk">
                      Dirección
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-1016" data-mp="1e97zj8iotnl5">
                      Vendedor
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-1017" data-mp="mi0i0k70i1vk">
                      Dependencia
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-1018" data-mp="2qlvfso80s33h">
                      Ruta Zona
                    </th>
                    <th className="px-4 py-3.5 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-1019" data-mp="2vey1ybagwa9n">
                      GPS
                    </th>
                    {isAdmin && <th className="px-4 py-3.5 w-16" data-id="element-1020" data-mp="27n109d0qeym9"></th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-1021" data-mp="2cm3us3j35mfz">
                  {paginatedClients.map(client => <tr key={client.id} className="hover:bg-gray-50/50 transition-colors" data-id="element-1022" data-mp="2hl6zfmv5lqnf">
                  
                      <td className="px-4 py-3.5 text-sm text-gray-600 font-mono whitespace-nowrap" data-id="element-1023" data-mp="3o1j0bvpd1mmp">
                        {client.codigo || '—'}
                      </td>
                      <td className="px-4 py-3.5" data-id="element-1024" data-mp="3t26qyg9og1ge">
                        <div className="flex items-center gap-3 min-w-[200px]" data-id="element-1025" data-mp="1hbyv5hlrv0ls">
                          <div className="p-2 bg-blue-50 rounded-lg flex-shrink-0" data-id="element-1026" data-mp="1cddwvnq2yqzz">
                            <BuildingIcon className="w-4 h-4 text-blue-600" data-id="element-1027" data-mp="17mxia0aiwzc1" />
                          </div>
                          <span className="font-semibold text-gray-900 text-sm truncate" title={client.nombre} data-id="element-1028" data-mp="12om8fpk8aott">
                        
                            {client.nombre}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap" data-id="element-1029" data-mp="tpsfc963wgl0">
                        {client.telefono || '—'}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap" data-id="element-1030" data-mp="jqjlrhf5l3z6">
                        {client.ciudad || '—'}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-900 font-medium whitespace-nowrap" data-id="element-1031" data-mp="2e2rgatnkbiuf">
                        {client.limite ? formatCurrency(client.limite) : '—'}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap" data-id="element-1032" data-mp="1rx9sc407xv6a">
                        {client.cedula || '—'}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap" data-id="element-1033" data-mp="1i770i74u2n9j">
                        {client.rnc || '—'}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap" data-id="element-1034" data-mp="145ulbxxhypop">
                        {client.ruta || '—'}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-600 max-w-[200px] truncate" title={client.direccion} data-id="element-1035" data-mp="u9rhmnmzyizn">
                    
                        {client.direccion}
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap" data-id="element-1036" data-mp="2ooc0currc4ge">
                        {client.vendedorNombre ? <div className="flex items-center gap-1.5 text-sm text-gray-700" data-id="element-1037" data-mp="2tmlx39t4rd44">
                            <UserIcon className="w-3.5 h-3.5 text-blue-400" data-id="element-1038" data-mp="hy8o5v024eg8" />
                            <span className="font-medium" data-id="element-1039" data-mp="czxblcidnpub">
                              {client.vendedorNombre}
                            </span>
                          </div> : <span className="text-xs text-gray-400 italic" data-id="element-1040" data-mp="rqecmur3odqo">
                            Sin asignar
                          </span>}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap" data-id="element-1041" data-mp="10oqb1iy7kfto">
                        {client.dependencia || '—'}
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap" data-id="element-1042" data-mp="1ano1wsj3wlfu">
                        <Badge variant="info" size="sm" data-id="element-1043" data-mp="1allbi7e1kqal">
                          {client.zona}
                        </Badge>
                      </td>
                      <td className="px-4 py-3.5 text-center whitespace-nowrap" data-id="element-1044" data-mp="3ce983xw6kfhe">
                        {client.lat && client.lng ? <div className="inline-flex items-center gap-2" data-id="element-1045" data-mp="37gzjulkj29v5">
                            <div className="inline-flex items-center gap-1.5" data-id="element-1046" data-mp="2geo95dqlv7kz">
                              <div className="w-2 h-2 rounded-full bg-emerald-500" data-id="element-1047" data-mp="2ccji8mrjkp21" />
                              <span className="text-xs text-emerald-600 font-medium" data-id="element-1048" data-mp="2cexc4sahjv80">
                                Sí
                              </span>
                            </div>
                            <a href={`https://www.google.com/maps?q=${client.lat},${client.lng}`} target="_blank" rel="noopener noreferrer" className="text-[10px] font-semibold text-blue-500 hover:text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded" title="Ver en Google Maps" data-id="element-1049" data-mp="22knlpg53t6zx">
                        
                              📍 Maps
                            </a>
                            <a href={`https://www.google.com/maps/dir/?api=1&destination=${client.lat},${client.lng}`} target="_blank" rel="noopener noreferrer" className="text-[10px] font-semibold text-green-600 hover:text-green-800 bg-green-50 px-1.5 py-0.5 rounded" title="Cómo llegar" data-id="element-1050" data-mp="1jp77rayno7p3">
                        
                              🧭 Ir
                            </a>
                          </div> : <div className="inline-flex items-center gap-1.5" data-id="element-1051" data-mp="3qjhad2q787pi">
                            <div className="w-2 h-2 rounded-full bg-gray-300" data-id="element-1052" data-mp="3vapkk0zcrx3q" />
                            <span className="text-xs text-gray-400" data-id="element-1053" data-mp="44y7a26oceyc">No</span>
                          </div>}
                      </td>
                      {isAdmin && <td className="px-4 py-3.5" data-id="element-1054" data-mp="32nlju87am4xr">
                          <Button variant="ghost" size="sm" onClick={() => openEditModal(client)} data-id="element-1055" data-mp="37ln6f33xgmdz">
                      
                            <EditIcon className="w-4 h-4 text-gray-400" data-id="element-1056" data-mp="3ck834sjz0nxo" />
                          </Button>
                        </td>}
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>}
      </div>

      {/* Pagination */}
      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={filteredClients.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-1057" data-mp="vw40rzrw3een" />
      

      {/* Add/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={closeModal} title={editingClient ? 'Editar Farmacia' : 'Nueva Farmacia'} size="lg" data-id="element-1058" data-mp="2nxnh9zm0zqh4">
        
        <ModalBody data-id="element-1059" data-mp="31wdf5hgo5u6j">
          <div className="flex gap-1 p-1 bg-gray-100 rounded-xl mb-6" data-id="element-1060" data-mp="31wwo48ocd88f">
            <button onClick={() => setActiveTab('info')} className={`flex-1 py-2 px-4 text-sm font-medium rounded-lg transition-colors ${activeTab === 'info' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-1061" data-mp="2wy3kd7yuwvlg">
              
              Información
            </button>
            <button onClick={() => setActiveTab('location')} className={`flex-1 py-2 px-4 text-sm font-medium rounded-lg transition-colors ${activeTab === 'location' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-1062" data-mp="2iyu1u1n6hpeb">
              
              Ubicación GPS
            </button>
          </div>

          {activeTab === 'info' && <div className="space-y-4" data-id="element-1063" data-mp="1lh3q46sy86os">
              <div className="grid grid-cols-2 gap-4" data-id="element-1064" data-mp="10vyj2o98htx">
                <Input label="Código" value={formData.codigo} onChange={e => setFormData({
              ...formData,
              codigo: e.target.value
            })} placeholder="Ej: CLI-001" data-id="element-1065" data-mp="z1d7n92mnpb" />
              
                <Input label="Nombre de la Farmacia" value={formData.nombre} onChange={e => setFormData({
              ...formData,
              nombre: e.target.value
            })} placeholder="Ej: Farmacia Central" data-id="element-1066" data-mp="3t4y77rk4pzah" />
              
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-1067" data-mp="oq6qpvrjh3wg">
                <Input label="RNC" value={formData.rnc} onChange={e => setFormData({
              ...formData,
              rnc: e.target.value
            })} placeholder="Ej: 101-12345-6" data-id="element-1068" data-mp="ar7btfttw3dh" />
              
                <Input label="Cédula" value={formData.cedula} onChange={e => setFormData({
              ...formData,
              cedula: e.target.value
            })} placeholder="Ej: 001-1234567-8" data-id="element-1069" data-mp="2vgmxxxi3r3ws" />
              
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-1070" data-mp="3frh8810v9hxw">
                <Input label="Teléfono" value={formData.telefono} onChange={e => setFormData({
              ...formData,
              telefono: e.target.value
            })} placeholder="809-555-1234" data-id="element-1071" data-mp="3aynzz366vrh2" />
              
                <Input label="Ciudad" value={formData.ciudad} onChange={e => setFormData({
              ...formData,
              ciudad: e.target.value
            })} placeholder="Ej: Santo Domingo" data-id="element-1072" data-mp="31zkk082bjs6s" />
              
              </div>

              <Input label="Dirección" value={formData.direccion} onChange={e => setFormData({
            ...formData,
            direccion: e.target.value
          })} placeholder="Ej: Calle El Conde #123" data-id="element-1073" data-mp="3ul2l92taq5vh" />
            

              <div className="grid grid-cols-2 gap-4" data-id="element-1074" data-mp="2e8nhtn0bs98q">
                <Input label="Ruta" value={formData.ruta} onChange={e => setFormData({
              ...formData,
              ruta: e.target.value
            })} placeholder="Ej: Ruta 1" data-id="element-1075" data-mp="2ea8a15z63g3f" />
              
                <Input label="Ruta Zona" value={formData.zona} onChange={e => setFormData({
              ...formData,
              zona: e.target.value
            })} placeholder="Ej: Zona Colonial" data-id="element-1076" data-mp="1z3sr73tv1437" />
              
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-1077" data-mp="2dvbeznsyo2x4">
                <Input label="Límite de Crédito (DOP)" type="number" value={formData.limite} onChange={e => setFormData({
              ...formData,
              limite: e.target.value
            })} placeholder="0.00" data-id="element-1078" data-mp="2dw4goad2c28e" />
              
                <Input label="Dependencia" value={formData.dependencia} onChange={e => setFormData({
              ...formData,
              dependencia: e.target.value
            })} placeholder="Ej: Casa Matriz" data-id="element-1079" data-mp="2ruuclmo5bvv6" />
              
              </div>

              {/* Vendedor asignado */}
              <div data-id="element-1080" data-mp="12fd0v5ixeqp1">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1081" data-mp="12euwflm41661">
                  Vendedor Asignado
                </label>
                <select value={formData.vendedorId} onChange={e => {
              const v = vendors.find(v => v.id === e.target.value);
              setFormData({
                ...formData,
                vendedorId: e.target.value,
                vendedorNombre: v?.nombre || ''
              });
            }} className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1082" data-mp="uf70ki7tix9t">
                
                  {vendorOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-1083" data-mp="1n3cjczfbac70">
                      {opt.label}
                    </option>)}
                </select>
              </div>
            </div>}

          {activeTab === 'location' && <div className="space-y-4" data-id="element-1084" data-mp="1w3ixmniu3g78">
              <div className="p-3 bg-blue-50 rounded-xl border border-blue-200" data-id="element-1085" data-mp="1w4k74nk7bumu">
                <p className="text-sm text-blue-700" data-id="element-1086" data-mp="1s40e9w6nvd6i">
                  <strong data-id="element-1087" data-mp="2ar96mpege7t8">Instrucciones:</strong> Busca una dirección, haz clic
                  en el mapa, o usa el botón de ubicación actual.
                  {hasGoogleApiKey() ? <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 bg-white rounded-lg text-xs font-semibold text-emerald-700 border border-emerald-200" data-id="element-1088" data-mp="2fnzw23c5efrb">
                      ✓ Google Maps Places activo
                    </span> : <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 bg-white rounded-lg text-xs font-semibold text-blue-700 border border-blue-200" data-id="element-1089" data-mp="1s0rqdkat54or">
                      🔍 Búsqueda con Photon + OpenStreetMap
                    </span>}
                </p>
              </div>

              <div className="flex gap-2" data-id="element-1090" data-mp="1n1evqddp2tbm">
                <div className="relative flex-1" data-id="element-1091" data-mp="1n283nhoe3ovc">
                  <div className="relative" data-id="element-1092" data-mp="191njah2ttkzq">
                    <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-1093" data-mp="192ynuj43iwrw" />
                    <input type="text" placeholder="Buscar en República Dominicana..." value={addressSearch} onChange={e => handleAddressSearch(e.target.value)} onFocus={() => searchResults.length > 0 && setShowResults(true)} onBlur={() => setTimeout(() => setShowResults(false), 200)} className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1094" data-mp="1e077yp8t1avs" />
                  
                    {isSearching && <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" data-id="element-1095" data-mp="1ak5nbmrtgdlf" />}
                  </div>
                  {showResults && searchResults.length > 0 && <div className="absolute z-[9999] top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden" data-id="element-1096" data-mp="10um2wcoggbog">
                      {searchResults.map((result, i) => <button key={i} onMouseDown={() => handleSelectAddress(result)} className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 border-b border-gray-100 last:border-0 transition-colors" data-id="element-1097" data-mp="rs37gdn011c8" data-mpi={i}>
                    
                          <div className="flex items-start gap-2" data-id="element-1098" data-mp="33grfz1qtyqvk">
                            <MapPinIcon className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" data-id="element-1099" data-mp="33ewwzsz4d8u4" />
                            <span className="line-clamp-2" data-id="element-1100" data-mp="38gm818mcdmi7">
                              {formatDisplayName(result.display_name)}
                            </span>
                          </div>
                        </button>)}
                      <a href={`https://www.google.com/maps/search/${encodeURIComponent(addressSearch)}`} target="_blank" rel="noopener noreferrer" onMouseDown={e => e.preventDefault()} className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold text-[#4285f4] hover:bg-blue-50 border-t border-gray-200 transition-colors" data-id="element-1101" data-mp="2kq9spcoronhl">
                    
                        <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" data-id="element-1102" data-mp="1x060e1h14a5w">
                      
                          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#EA4335" data-id="element-1103" data-mp="2106etm44olcy" />
                      
                          <circle cx="12" cy="9" r="2.5" fill="white" data-id="element-1104" data-mp="kl0rmwux4a5a" />
                        </svg>
                        Buscar "{addressSearch}" en Google Maps
                        <svg className="w-3.5 h-3.5 ml-auto flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-1105" data-mp="pj2c9m7zshiv">
                      
                          <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" data-id="element-1106" data-mp="za6r8lxo42dp" />
                        </svg>
                      </a>
                    </div>}
                  {showResults && searchResults.length === 0 && !isSearching && addressSearch.length >= 2 && <div className="absolute z-[9999] top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden" data-id="element-1107" data-mp="1d96cdjlz1z54">
                        <div className="p-4 text-sm text-gray-500 text-center" data-id="element-1108" data-mp="3f1uflsym8mkw">
                          No se encontraron resultados
                        </div>
                        <a href={`https://www.google.com/maps/search/${encodeURIComponent(addressSearch)}`} target="_blank" rel="noopener noreferrer" onMouseDown={e => e.preventDefault()} className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold text-[#4285f4] hover:bg-blue-50 border-t border-gray-200 transition-colors" data-id="element-1109" data-mp="37jmn4ktt44lh">
                    
                          <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" data-id="element-1110" data-mp="3qhfq1n4fkeiv">
                      
                            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#EA4335" data-id="element-1111" data-mp="3lim9tmxre9f0" />
                      
                            <circle cx="12" cy="9" r="2.5" fill="white" data-id="element-1112" data-mp="3chofjwd4hg9m" />
                          </svg>
                          Buscar "{addressSearch}" en Google Maps
                          <svg className="w-3.5 h-3.5 ml-auto flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-1113" data-mp="251qd4qysy14w">
                      
                            <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" data-id="element-1114" data-mp="22kq0842dnmmp" />
                          </svg>
                        </a>
                      </div>}
                </div>
                <Button variant="secondary" onClick={handleUseCurrentLocation} isLoading={isGettingLocation} leftIcon={<NavigationIcon className="w-4 h-4" data-id="element-1116" data-mp="1xih6v6lleik2" />} data-id="element-1115" data-mp="1skz5q2dth7mm">
                
                  Mi ubicación
                </Button>
              </div>

              {locationError && <div className="flex items-center gap-2 p-3 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-700" data-id="element-1117" data-mp="1okoykmeagb66">
                  <span data-id="element-1118" data-mp="19wkepf3i1tlt">⚠️</span>
                  <span data-id="element-1119" data-mp="1etklxtc352ei">{locationError}</span>
                </div>}

              <div className="grid grid-cols-2 gap-3" data-id="element-1120" data-mp="1eu3vomrl0khg">
                <div data-id="element-1121" data-mp="10tt1pauz6ps2">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1122" data-mp="10vckb843tcx0">
                    Latitud
                  </label>
                  <div className="block w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 font-mono" data-id="element-1123" data-mp="14xhce2ewm1hj">
                    {formData.lat ? formData.lat.toFixed(6) : <span className="text-gray-400" data-id="element-1124" data-mp="hacakufnan7v">—</span>}
                  </div>
                </div>
                <div data-id="element-1125" data-mp="v5xvoqircmv0">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1126" data-mp="q97g04av7ta4">
                    Longitud
                  </label>
                  <div className="block w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 font-mono" data-id="element-1127" data-mp="154x5wdj68qk4">
                    {formData.lng ? formData.lng.toFixed(6) : <span className="text-gray-400" data-id="element-1128" data-mp="1dys22w2bf6nm">—</span>}
                  </div>
                </div>
              </div>

              <div className="h-[300px] w-full rounded-xl border border-gray-200 overflow-hidden relative z-0" data-id="element-1129" data-mp="19xgjvblrtnpg">
                <LeafletMap onMapReady={handleMapReady} center={formData.lat && formData.lng ? [formData.lat, formData.lng] : undefined} data-id="element-1130" data-mp="1nwzdkzdu4wi8" />
              
              </div>

              {formData.lat && formData.lng && <div className="space-y-3" data-id="element-1131" data-mp="22xn2vvix678q">
                  <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-xl border border-emerald-200" data-id="element-1132" data-mp="270t8d0y7jue5">
                    <CheckCircleIcon className="w-5 h-5 text-emerald-600 flex-shrink-0" data-id="element-1133" data-mp="2bt2su1gk0291" />
                    <div className="flex-1" data-id="element-1134" data-mp="2gre1i8v446po">
                      <p className="text-sm font-medium text-emerald-800" data-id="element-1135" data-mp="2gt8m1m2h1wt8">
                        Ubicación seleccionada
                      </p>
                      <p className="text-xs text-emerald-600 font-mono" data-id="element-1136" data-mp="1o1oyxr2r7yly">
                        {formData.lat.toFixed(6)}, {formData.lng.toFixed(6)}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2" data-id="element-1137" data-mp="1y1ftfsrbedm1">
                    <a href={`https://www.google.com/maps?q=${formData.lat},${formData.lng}`} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-[#4285f4] hover:bg-[#3367d6] text-white rounded-xl text-sm font-semibold transition-colors" data-id="element-1138" data-mp="34gp4zy3ukf7i">
                  
                      📍 Ver en Google Maps
                    </a>
                    <a href={`https://www.google.com/maps/dir/?api=1&destination=${formData.lat},${formData.lng}`} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-[#34a853] hover:bg-[#2d9249] text-white rounded-xl text-sm font-semibold transition-colors" data-id="element-1139" data-mp="svppv35dghfg">
                  
                      🧭 Cómo llegar
                    </a>
                  </div>
                </div>}
            </div>}
        </ModalBody>
        <ModalFooter data-id="element-1140" data-mp="5412803hwh4u">
          <div className="flex items-center justify-between w-full" data-id="element-1141" data-mp="4piqjbquxjk">
            {editingClient && isAdmin ? <Button variant="danger" onClick={() => setIsDeleteModalOpen(true)} leftIcon={<TrashIcon className="w-4 h-4" data-id="element-1143" data-mp="1zh0mw7mqg1iz" />} data-id="element-1142" data-mp="726xe83i452">
              
                Eliminar
              </Button> : <div data-id="element-1144" data-mp="1ujifwms9zwdm" />}
            <div className="flex items-center gap-3" data-id="element-1145" data-mp="3oyxkgczsldv2">
              <Button variant="secondary" onClick={closeModal} data-id="element-1146" data-mp="3oy5q15hxlm6d">
                Cancelar
              </Button>
              <Button onClick={handleSave} isLoading={isSaving} disabled={!formData.nombre || !formData.zona || !formData.direccion} data-id="element-1147" data-mp="3tr8mpmi09bqs">
                
                {editingClient ? 'Guardar Cambios' : 'Crear Farmacia'}
              </Button>
            </div>
          </div>
        </ModalFooter>
      </Modal>

      <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} title="Eliminar Farmacia" size="sm" data-id="element-1148" data-mp="bhy8hskf210s">
        
        <ModalBody data-id="element-1149" data-mp="2jmklyaxphc1">
          <p className="text-gray-600" data-id="element-1150" data-mp="2k4e4n67phg9">
            ¿Estás seguro de que deseas eliminar{' '}
            <strong data-id="element-1151" data-mp="6k6jb6b5ot0a">{editingClient?.nombre}</strong>? Esta acción no se puede
            deshacer.
          </p>
        </ModalBody>
        <ModalFooter data-id="element-1152" data-mp="2n80ljiohaezw">
          <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)} data-id="element-1153" data-mp="2nboby8ymmlae">
            
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleDelete} isLoading={isSaving} data-id="element-1154" data-mp="2jr4pp00ynii6">
            Eliminar
          </Button>
        </ModalFooter>
      </Modal>
    </div>;
}